/**
 * The on-disk cache for decoded transaction bodies.
 *
 * Bodies are content-addressed by their S3 etag, so caching them in IndexedDB is
 * free correctness-wise: a re-decode mints a new etag and a new key, and stale
 * data is never served. What it is NOT free of is a privacy obligation — a
 * shared or handed-over browser would otherwise keep one user's decoded
 * transactions on disk for whoever signs in next. Erasing it is
 * `./browserData`'s job, together with the rest of an identity's browser data;
 * it runs on every sign-out and tenant switch (`SessionManager.onIdentityChange`)
 * and prunes once a session is established.
 *
 * Identity comes from the live routing token rather than React, because the
 * teardown runs during sign-out, when the provider tree is on its way out.
 * Mirrors `cdn.ts`, which resolves its scope the same way and for the same
 * reason.
 */
import {
  createScopedTxCache,
  type ScopedTxCache,
  type StorageIdentity,
} from "@authe/fe-commons/storage";
import { tenantContextFromClaims } from "@authe/fe-commons/auth";
import { appEnv, APPLICATION_SLUG } from "@/config";
import { getRoutingToken } from "./routing-token";

/** The signed-in identity, from the live routing token (anonymous before sign-in). */
export function currentIdentity(): StorageIdentity {
  const token = getRoutingToken();
  const t = token
    ? tenantContextFromClaims(token as Record<string, unknown>, appEnv.claimsNamespace)
    : null;
  return {
    app: APPLICATION_SLUG,
    customerId: t?.customerId ?? null,
    orgId: t?.orgId ?? null,
    tenantId: t?.tenantId ?? null,
    userId: t?.userId ?? null,
  };
}

/**
 * The cache for the identity currently signed in.
 *
 * Rebuilt per call rather than memoised: the identity changes under us on a
 * tenant switch, and a cache pinned to the identity that happened to be current
 * at module load would write the new tenant's bodies under the old tenant's key
 * prefix. Construction is a couple of string joins over the page's one shared
 * IndexedDB handle (`createScopedTxCache` holds it), so there is nothing to save
 * by holding onto one.
 */
export function txCache(): ScopedTxCache {
  return createScopedTxCache(currentIdentity());
}
